import React from 'react';
import { Modal as AntdModal } from 'antd';
import { CloseLarge, Info,  Danger, Error, CircleCheck } from '@nackle/origami-icons';
import { Button } from '../button/button';
import classNames from 'classnames';

const Title = ( props ) => {
    const { title, subTitle } = props;
    if ( !title && !subTitle ) {
        return <></>;
    }
    if ( !subTitle ) {
        return <>{ title }</>;
    }
    return (
        <>
            { title }
            <div className={ 'ant-modal-subtitle' }>
                { subTitle }
            </div>
        </>
    );
};

/**
 * A modal
 *
 * {@link https://ant.design/components/modal#api}
 *
 * @module Modal
 * @param {Object} props - the modal props
 * @param {string} [props.title] - the modal title
 * @param {string} [props.subTitle] - the modal subtitle
 * @param {number} [props.mobileHeight] - the modal height
 * @param {Function} [props.afterOpenChange] - the modal after open change callback
 * @param {string} [props.className] - the modal class name
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement} - a modal component
**/
const Modal = ( { children, ...props } ) => {
    const { title, subTitle, className, mobileHeight, afterOpenChange } = props;
    const modalClasses = classNames( {
        'full-height': mobileHeight === 'auto' ? false : true,
        [ className ]: className ? true : false
    } );
    const handleOpenChange = ( open ) => {
        if ( open ) {
            document.body.style.overflowY = 'hidden';
            document.documentElement.style.overflowY = 'hidden';
        } else {
            document.body.style.overflowY = 'auto';
            document.documentElement.style.overflowY = 'auto';
        }
        if ( typeof afterOpenChange === 'function' ) {
            afterOpenChange( open );
        }
    };
    return (
        <AntdModal afterOpenChange={ handleOpenChange } closeIcon={ <CloseLarge width={ 20 } height={ 20 } /> } centered={ true } { ...props } title={ <Title title={ title } subTitle={ subTitle } /> } className={ modalClasses }>
            { children }
        </AntdModal>
    );
};

/**
  * A confirm modal
  * @module [Modal.confirm]
  * @param {Object} config - the confirm config
  * @param {string} [config.title] - the confirm title
  * @param {React.ReactElement} [config.closeIcon] - the confirm close icon
**/
Modal.confirm = ( config ) => {
    const { title, closeIcon } = config;
    const modal =  AntdModal.confirm( {
        ...config,
        icon: null,
        centered: config.centered || true,
        title: (
            <div className='confirm-modal-header'>
                <div className='confirm-modal-title'>{ title }</div>
                {
                    closeIcon !== false &&
                    <Button onClick={ ()=> modal.destroy() } className='confirm-modal-header-close' aria-label="Close" shape='circle' icon={ closeIcon ? closeIcon :  <CloseLarge/> } type='text'/>
                }
            </div>
        )
    } );
    return modal;
};

/**
 * An info modal
 * @module [Modal.info]
 * @param {string} [config.title] - the modal title
 * @param {React.ReactElement} [config.closeIcon] - the modal icon
 * @returns {React.ReactElement} - an info modal
**/
Modal.info = ( config ) => {
    const { title, closeIcon } = config;
    const modal = AntdModal.info( {
        ...config,
        icon: null,
        centered: config.centered || true,
        title: (
            <div className='confirm-modal-header'>
                { !config.hideIcon && <Info className='ant-modal-icon info'/> }
                <div className='confirm-modal-title'>{ title }</div>
                {
                    closeIcon !== false &&
                    <Button onClick={ ()=> modal.destroy() } className='confirm-modal-header-close' aria-label="Close" shape='circle' icon={ closeIcon ? closeIcon :  <CloseLarge/> } type='text'/>
                }
            </div>
        )
    } );
    return modal;
};

/**
 * A success modal
 * @module [Modal.success]
 * @param {string} [config.title] - the modal title
 * @param {React.ReactElement} [config.closeIcon] - the modal icon
 * @returns {React.ReactElement} - a success modal
**/
Modal.success = ( config ) => {
    const { title } = config;
    const modal = AntdModal.success( {
        ...config,
        icon: null,
        centered: config.centered || true,
        title: (
            <div className='confirm-modal-header'>
                <CircleCheck className='ant-modal-icon success'/>
                <div className='confirm-modal-title'>{ title }</div>
                <Button onClick={ ()=> modal.destroy() } className='confirm-modal-header-close' aria-label="Close" shape='circle' icon={ <CloseLarge/> } type='text'/>
            </div>
        )
    } );
    return modal;
};

/**
 * An error modal
 * @module [Modal.error]
 * @param {string} [config.title] - the modal title
 * @param {React.ReactElement} [config.closeIcon] - the modal icon
 * @returns {React.ReactElement} - an error modal
**/
Modal.error = ( config ) => {
    const { title, closeIcon } = config;
    const modal = AntdModal.error( {
        ...config,
        icon: null,
        centered: config.centered || true,
        title: (
            <div className='confirm-modal-header'>
                <Error className='ant-modal-icon error'/>
                <div className='confirm-modal-title'>{ title }</div>
                {
                    closeIcon !== false &&
                    <Button onClick={ ()=> modal.destroy() } className='confirm-modal-header-close' aria-label="Close" shape='circle' icon={ closeIcon ? closeIcon :  <CloseLarge/> } type='text'/>
                }
            </div>
        )
    } );
    return modal;
};

/**
  * A warning modal
  * @module {Modal.warning}
  * @param {string} [config.title] - the modal title
  * @param {React.ReactElement} [config.closeIcon]- the modal icon
  * @returns {React.ReactElement} - a warning modal
**/
Modal.warning = ( config ) => {
    const { title, closeIcon } = config;
    const modal =  AntdModal.warning( {
        ...config,
        icon: null,
        centered: config.centered || true,
        title: (
            <div className='confirm-modal-header'>
                <Danger className='ant-modal-icon warning'/>
                <div className='confirm-modal-title'>{ title }</div>
                {
                    closeIcon !== false &&
                    <Button onClick={ ()=> modal.destroy() } className='confirm-modal-header-close' aria-label="Close" shape='circle' icon={ closeIcon ? closeIcon :  <CloseLarge/> } type='text'/>
                }
            </div>
        )
    } );
    return modal;
};

export { Modal };
